"use client";

import * as React from "react";
import { Loader2 } from "lucide-react";
import { formatABN } from "@/lib/abn-lookup";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export interface AutocompleteOption {
  value: string;
  label: string;
  description?: string;
  metadata?: Record<string, string>;
}

interface AutocompleteProps {
  options: AutocompleteOption[];
  value?: string;
  onValueChange?: (value: string, option?: AutocompleteOption) => void;
  onSearch?: (query: string) => void;
  placeholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;

  allowCustomValue?: boolean;
  renderOption?: (option: AutocompleteOption) => React.ReactNode;
}

export function Autocomplete({
  options,
  value,
  onValueChange,
  onSearch,
  placeholder = "Select an option...",
  emptyText = "No results found.",
  loading = false,
  disabled = false,
  className,
  allowCustomValue = false,
  renderOption,
}: AutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedOption = React.useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  // Sync inputValue with selectedOption
  React.useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else if (value) {
      setInputValue(value);
    } else {
      setInputValue(""); // Clear input when no value is selected
    }
  }, [selectedOption, value]);


  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      const option = options.find((opt) => opt.value === selectedValue);
      onValueChange?.(selectedValue, option);
      setInputValue(option?.label || selectedValue);
      setOpen(false);
    },
    [options, onValueChange]
  );

  const handleCustomValueSelect = React.useCallback(() => {
    if (allowCustomValue && inputValue && !options.some(opt => opt.value === inputValue)) {
      onValueChange?.(inputValue, {
        value: inputValue,
        label: inputValue,
      });
      setOpen(false);
    }
  }, [allowCustomValue, inputValue, options, onValueChange]);

  return (
    <div className="relative" ref={containerRef}>
      <Command className="rounded-md" shouldFilter={false}>
        <div className="relative">
          <CommandInput
            ref={inputRef}
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value);
              onSearch?.(value);
              
              // Clear the selected value when user types
              if (value !== selectedOption?.label) {
                onValueChange?.("", undefined);
              }
              
              if (value.length > 0) {
                setOpen(true);
              } else {
                setOpen(false);
                // Clear options when input is empty
                onSearch?.("");
              }
            }}
            onFocus={() => inputValue.length > 0 && setOpen(true)}
            onBlur={() => {
              // Small delay to allow click events on suggestions to fire
              setTimeout(() => setOpen(false), 200);
            }}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "pr-10",
               "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
              // disabled && "bg-muted",
              className
            )}
            autoComplete="off"
          />
          {/* <button
            type="button"
            className={cn(
              "absolute inset-y-0 right-0 flex items-center pr-3",
              disabled && "cursor-not-allowed opacity-50"
            )}
            disabled={disabled}
            tabIndex={-1}
            onClick={() => setOpen(!open)}
          >
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button> */}
        </div>
        
        {open && (
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 paragraph-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            ) : options.length > 0 ? (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={handleSelect}
                    className="cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      {renderOption ? renderOption(option) : (
                        <div className="flex items-start p-2">
                          <div className="paragraph-base  min-w-0 flex-1">
                            <div className="truncate">{option.label}</div>
                            <div className="flex items-center space-x-2 text-muted-foreground">
                              <span>ABN: {formatABN(option.metadata?.abn || "")}</span>
                              {option.metadata?.state && (
                                <>
                                  <div className="flex items-center space-x-1">
                                    <span className='material-symbols-outlined'>location_on</span>
                                    {option.metadata.postcode && <span>{option.metadata.postcode}</span>}
                                    <span>{option.metadata.state}</span>
                                  </div>
                                </>
                              )}
                              
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CommandItem>
                ))}
                {allowCustomValue && 
                 inputValue && 
                 !options.some(opt => opt.value === inputValue) && (
                  <CommandItem
                    value={inputValue}
                    onSelect={() => handleCustomValueSelect()}
                    className="cursor-pointer border-t"
                  >
                    <div className="flex-1">
                      <div className="paragraph-sm">
                        Use &quot;{inputValue}&quot;
                      </div>
                      <div className="paragraph-sm text-muted-foreground">
                        Custom value
                      </div>
                    </div>
                  </CommandItem>
                )}
              </CommandGroup>
            ) : (
              <CommandEmpty>
                <div className="py-6 text-center">
                  <p className="paragraph-sm text-muted-foreground px-4">
                    {emptyText}
                  </p>
                </div>
              </CommandEmpty>
            )}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
